// ─── CsvColumnMapper ─────────────────────────────────────────────────────────
//
// A reusable UI for mapping the columns of an arbitrary CSV file to the
// fields Receipt Chaser expects (date, description, amount, payee).
//
// Used as a fallback when auto-detection fails, or when the user opts in
// via the "Custom mapping" choice on the software dropdown.
//
// Props:
//   rows         : 2D array of strings (the raw CSV parsed into rows)
//   initialMapping (optional) : a mapping object to seed the form with;
//                                if omitted, suggestMapping() is called
//   onCancel     : callback when the user clicks Back / Cancel
//   onApply      : callback receiving (transactions, mapping) when the user
//                  commits the mapping. transactions is the produced array;
//                  mapping is the chosen config (for potential save later).
//
// The component handles its own state (header row, column choices, etc.).
// Caller only sees the final result.
//
// Layout:
//   1. Header row picker (which row contains column headers)
//   2. Sample data preview (first 5 rows with selected headers)
//   3. Field mappings (date / description / amount / payee)
//   4. Live preview of parsed output
//   5. Apply / Cancel buttons

import { useState, useMemo } from 'react'
import { parseWithMapping, suggestMapping, validateMapping, formatCurrency, formatDate } from '../lib/csvParser'

export default function CsvColumnMapper({ rows, initialMapping, onCancel, onApply }) {
  const [mapping, setMapping] = useState(() => initialMapping || suggestMapping(rows))

  // Header row drives what the column dropdowns show as labels. Whenever
  // the user changes which row is the header, the dropdown labels update.
  const headerCells = (rows[mapping.headerRowIndex] || []).map((c, i) => ({
    index: i,
    label: (c || '').toString().trim() || `Column ${i + 1}`,
  }))

  // Run the parser on the current mapping to produce a live preview.
  // useMemo so we don't reparse on unrelated state changes.
  const previewTransactions = useMemo(() => {
    try {
      return parseWithMapping(rows, mapping)
    } catch (err) {
      // If the mapping is incomplete the parser may throw; show empty
      // preview in that case. Validation will block the Apply button.
      return []
    }
  }, [rows, mapping])

  const validationError = validateMapping(mapping)

  const update = (patch) => setMapping(m => ({ ...m, ...patch }))

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div style={{
      background: 'var(--white)',
      border: '1px solid var(--surface-2)',
      borderRadius: 'var(--radius)',
      padding: '20px 24px',
    }}>
      <div style={{ marginBottom: 18 }}>
        <h3 style={{ margin: 0, fontSize: '1.05rem', marginBottom: 4 }}>
          Map your CSV columns
        </h3>
        <p style={{ margin: 0, fontSize: '0.86rem', color: 'var(--ink-muted)', lineHeight: 1.5 }}>
          Tell us which column in your file holds each piece of information.
          A live preview at the bottom shows how each row will be read.
        </p>
      </div>

      {/* ─── Header row picker ───────────────────────────────────────── */}
      <Section title="1. Which row contains column headers?">
        <p style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
          Skip metadata rows by picking the row where your column names appear.
          Data starts from the row immediately after.
        </p>
        <RowPicker
          rows={rows.slice(0, Math.min(rows.length, 10))}
          selectedIndex={mapping.headerRowIndex}
          onSelect={i => update({ headerRowIndex: i })}
        />
      </Section>

      {/* ─── Column field mappings ──────────────────────────────────── */}
      <Section title="2. Which column holds what?">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '12px 16px',
          marginBottom: 14,
        }}>
          <ColumnDropdown
            label="Date *"
            value={mapping.dateColumn}
            onChange={v => update({ dateColumn: v })}
            options={headerCells}
          />
          <ColumnDropdown
            label="Description *"
            value={mapping.descriptionColumn}
            onChange={v => update({ descriptionColumn: v })}
            options={headerCells}
          />
          <ColumnDropdown
            label="Payee (optional)"
            value={mapping.payeeColumn}
            onChange={v => update({ payeeColumn: v })}
            options={headerCells}
            allowNone
          />
        </div>

        {/* ─── Amount section: single vs split ─────────────────────── */}
        <div style={{
          marginTop: 10,
          padding: '12px 14px',
          background: 'var(--surface)',
          borderRadius: 8,
        }}>
          <div style={{
            fontSize: '0.74rem', fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.04em',
            color: 'var(--ink-muted)', marginBottom: 8,
          }}>
            Amount
          </div>
          <div style={{
            display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap',
            fontSize: '0.85rem',
          }}>
            <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <input
                type="radio"
                name="amountMode"
                checked={mapping.amountMode === 'single'}
                onChange={() => update({ amountMode: 'single' })}
              />
              One signed column (e.g. "Amount")
            </label>
            <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <input
                type="radio"
                name="amountMode"
                checked={mapping.amountMode === 'split'}
                onChange={() => update({ amountMode: 'split' })}
              />
              Separate money-in / money-out columns
            </label>
          </div>

          {mapping.amountMode === 'single' ? (
            <ColumnDropdown
              label="Amount column *"
              value={mapping.amountColumn}
              onChange={v => update({ amountColumn: v })}
              options={headerCells}
            />
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 12,
            }}>
              <ColumnDropdown
                label="Money in"
                value={mapping.moneyInColumn}
                onChange={v => update({ moneyInColumn: v })}
                options={headerCells}
                allowNone
              />
              <ColumnDropdown
                label="Money out"
                value={mapping.moneyOutColumn}
                onChange={v => update({ moneyOutColumn: v })}
                options={headerCells}
                allowNone
              />
            </div>
          )}

          {/* Reverse sign toggle.
              Credit card statements typically show expenditure as positive
              numbers; ticking this flips every parsed amount so spending
              ends up negative (matching the rest of Receipt Chaser's
              convention where amount > 0 means income). */}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            marginTop: 12, padding: '10px 12px',
            background: 'var(--white)', border: '1px solid var(--surface-2)',
            borderRadius: 6, cursor: 'pointer',
            fontSize: '0.85rem',
          }}>
            <input
              type="checkbox"
              checked={!!mapping.reverseSign}
              onChange={e => update({ reverseSign: e.target.checked })}
              style={{ marginTop: 2 }}
            />
            <span>
              <strong style={{ display: 'block', marginBottom: 2 }}>
                Reverse sign of all amounts
              </strong>
              <span style={{ color: 'var(--ink-muted)', fontSize: '0.82rem' }}>
                Tick this if your file shows expenditure as positive numbers
                (common on credit card statements). The preview below updates
                as you toggle.
              </span>
            </span>
          </label>
        </div>

        {/* ─── Date format ─────────────────────────────────────────── */}
        <div style={{ marginTop: 14 }}>
          <FieldLabel>Date format</FieldLabel>
          <select
            className="input"
            value={mapping.dateFormat || 'auto'}
            onChange={e => update({ dateFormat: e.target.value })}
            style={{ maxWidth: 300 }}
          >
            <option value="auto">Auto-detect</option>
            <option value="DMY">DD/MM/YYYY (UK)</option>
            <option value="MDY">MM/DD/YYYY (US)</option>
            <option value="YMD">YYYY-MM-DD (ISO)</option>
          </select>
          <p style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', margin: '6px 0 0' }}>
            Pick the format your CSV uses. Auto-detect works for most cases.
          </p>
        </div>
      </Section>

      {/* ─── Live preview ───────────────────────────────────────────── */}
      <Section title={`3. Preview (${previewTransactions.length} transaction${previewTransactions.length === 1 ? '' : 's'} found)`}>
        {validationError ? (
          <div style={{
            padding: '10px 14px',
            background: 'var(--amber-light)',
            color: 'var(--amber)',
            borderRadius: 8,
            fontSize: '0.86rem',
          }}>
            ⚠ {validationError}
          </div>
        ) : previewTransactions.length === 0 ? (
          <div style={{
            padding: '14px 18px',
            background: 'var(--surface)',
            color: 'var(--ink-muted)',
            borderRadius: 8,
            fontSize: '0.86rem',
            lineHeight: 1.5,
          }}>
            No usable rows with the current mapping. Check that your date and
            amount columns are correctly picked, and that the date format
            matches what's in your file.
          </div>
        ) : (
          <div style={{
            maxHeight: 260, overflowY: 'auto',
            border: '1px solid var(--surface-2)', borderRadius: 8,
          }}>
            <table style={{ width: '100%', fontSize: '0.83rem' }}>
              <thead>
                <tr style={{ background: 'var(--surface)' }}>
                  <th style={{ textAlign: 'left',  padding: '8px 10px', fontWeight: 600 }}>Date</th>
                  <th style={{ textAlign: 'left',  padding: '8px 10px', fontWeight: 600 }}>Description</th>
                  <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {previewTransactions.slice(0, 50).map((t, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--surface-2)' }}>
                    <td style={{ padding: '6px 10px', color: 'var(--ink-muted)' }}>{formatDate(t.date)}</td>
                    <td style={{ padding: '6px 10px' }}>{t.description}</td>
                    <td style={{
                      padding: '6px 10px',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color: t.amount > 0 ? 'var(--green)' : 'var(--ink)',
                      fontWeight: 600,
                    }}>
                      {t.amount > 0 ? '+' : ''}{formatCurrency(t.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {previewTransactions.length > 50 && (
              <div style={{
                padding: '8px 12px', fontSize: '0.78rem',
                color: 'var(--ink-faint)', textAlign: 'center',
                background: 'var(--surface)',
              }}>
                Showing first 50 of {previewTransactions.length}. All rows will be imported.
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ─── Actions ────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost" onClick={onCancel}>
          Back
        </button>
        <button
          className="btn btn-primary"
          onClick={() => onApply(previewTransactions, mapping)}
          disabled={!!validationError || previewTransactions.length === 0}
        >
          Use these {previewTransactions.length} transaction{previewTransactions.length === 1 ? '' : 's'}
        </button>
      </div>
    </div>
  )
}

// ─── Small internal helpers ──────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <h4 style={{ margin: '0 0 10px', fontSize: '0.92rem', fontWeight: 600 }}>{title}</h4>
      {children}
    </div>
  )
}

function FieldLabel({ children }) {
  return (
    <div style={{
      fontSize: '0.74rem', fontWeight: 600,
      textTransform: 'uppercase', letterSpacing: '0.04em',
      color: 'var(--ink-muted)', marginBottom: 4,
    }}>
      {children}
    </div>
  )
}

function ColumnDropdown({ label, value, onChange, options, allowNone }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <select
        className="input"
        value={value ?? -1}
        onChange={e => onChange(parseInt(e.target.value, 10))}
      >
        {allowNone && <option value={-1}>(not mapped)</option>}
        {!allowNone && (value === -1 || value == null) && (
          <option value={-1}>(pick a column)</option>
        )}
        {options.map(opt => (
          <option key={opt.index} value={opt.index}>{opt.label}</option>
        ))}
      </select>
    </div>
  )
}

// Visual row picker showing the first N rows of the CSV. Click a row to mark
// it as the header. Selected row gets an outline and an icon. Cells in the
// selected row are styled like column headers (uppercase, muted) so the user
// can preview how the labels will look in the column dropdowns above.
function RowPicker({ rows, selectedIndex, onSelect }) {
  if (!rows || rows.length === 0) return null
  return (
    <div style={{
      border: '1px solid var(--surface-2)', borderRadius: 8,
      overflow: 'hidden',
    }}>
      <table style={{ width: '100%', fontSize: '0.8rem', tableLayout: 'fixed' }}>
        <tbody>
          {rows.map((row, i) => {
            const isSelected = i === selectedIndex
            return (
              <tr
                key={i}
                onClick={() => onSelect(i)}
                style={{
                  cursor: 'pointer',
                  background: isSelected ? 'var(--accent-light)' : (i % 2 === 0 ? 'var(--white)' : 'var(--surface)'),
                  borderTop: i === 0 ? 'none' : '1px solid var(--surface-2)',
                  borderLeft: isSelected ? '3px solid var(--accent)' : '3px solid transparent',
                }}
                title={isSelected ? 'This is the header row' : 'Click to mark this as the header row'}
              >
                <td style={{
                  width: 28, padding: '6px 8px',
                  color: isSelected ? 'var(--accent)' : 'var(--ink-faint)',
                  fontSize: '0.7rem', fontWeight: 700,
                  textAlign: 'center',
                }}>
                  {isSelected ? '▸' : i + 1}
                </td>
                {row.slice(0, 8).map((cell, j) => (
                  <td key={j} style={{
                    padding: '6px 8px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? 'var(--accent)' : 'var(--ink)',
                    textTransform: isSelected ? 'uppercase' : 'none',
                    fontSize: isSelected ? '0.72rem' : '0.8rem',
                    letterSpacing: isSelected ? '0.03em' : 'normal',
                  }}>
                    {cell || ''}
                  </td>
                ))}
                {row.length > 8 && (
                  <td style={{ padding: '6px 8px', color: 'var(--ink-faint)', fontSize: '0.7rem' }}>
                    +{row.length - 8}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
